import { loadStripe, Stripe } from "@stripe/stripe-js"
import React, { useContext, useEffect, useState } from "react"
import { CampProps } from "../../interfaces"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { ModalContext } from "../../contexts/admin/ModalContext"
import Button from "../ui/Button"
import api from "../../api2"
import ICamper from "../../interfaces/ICamper"
import Spinner from "../ui/Spinner"

const stripePublicKey = import.meta.env.VITE_STRIPE_PUBKEY

interface ComponentProps {
  camp: CampProps
  camper: ICamper
  onSuccess: () => void
  onClose: () => void
}

const StripeCardSetupForm = ({ camp, camper, onSuccess, onClose }: ComponentProps) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe>>()
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  useEffect(() => {
    setStripePromise(loadStripe(stripePublicKey, { stripeAccount: camp.stripe_account_id }))

    api.Campers.setupIntent(camper.id)
      .then(async (response) => {
        setClientSecret(response.clientSecret)
      })
      .catch((error) => {
        console.error(error)
      })
  }, [])

  return (
    <div className="w-full flex flex-col items-center justify-center">
      {!stripePromise || !clientSecret ? (
        <Spinner size={5} />
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret: clientSecret }}>
          <FormContainer camp={camp} onSuccess={onSuccess} onClose={onClose} />
        </Elements>
      )}
    </div>
  )
}

const FormContainer = ({ camp, onSuccess, onClose }: Omit<ComponentProps, "camper">) => {
  const stripePromise = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)

  const handleClickSave = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()

    setIsLoading(true)

    if (!stripePromise || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null
    }

    const { error } = await stripePromise.confirmSetup({
      elements,
      redirect: "if_required",
    })

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      console.log(error.message)
    } else {
      // Show a success message to your customer
      onClose()
    }

    setIsLoading(false)
  }

  const handleClickCancel = () => {
    onClose()
  }

  return (
    <div className="flex flex-col">
      <div className="my-4">
        <PaymentElement />
      </div>
      <div className="sm:flex sm:flex-row justify-end items-center">
        <div className="flex sm:inline mt-2 sm:mt-0">
          <Button type="button" variant="gray" className="mr-2.5" disabled={isLoading} onClick={handleClickCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="blue" disabled={isLoading} onClick={handleClickSave}>
            Add payment method
          </Button>
        </div>
      </div>
    </div>
  )
}

export default StripeCardSetupForm
